import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  DEFAULT_PORTAL_CONFIG,
  subscribeToPortalConfig,
  savePortalConfig,
} from '../services/portalConfigService';

const NOTIFY_OPTIONS = [
  { value: 'both', label: 'Both activations and failures' },
  { value: 'activation', label: 'Activations only' },
  { value: 'failure', label: 'Failures only' },
  { value: 'none', label: 'None' },
];

const PortalAutomationSettings = () => {
  const user = useAuthStore((s) => s.user);
  const [config, setConfig] = useState(DEFAULT_PORTAL_CONFIG);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = subscribeToPortalConfig((next) => {
      // Only apply remote updates when we don't have pending local edits
      if (!dirty) setConfig(next);
    });
    return () => unsub && unsub();
  }, [dirty]);

  const update = (patch) => {
    setConfig((c) => ({ ...c, ...patch }));
    setDirty(true);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await savePortalConfig(
        {
          autoActivateOnCardAttach: !!config.autoActivateOnCardAttach,
          notifyAdminOn: config.notifyAdminOn || 'both',
          adminNotificationEmail: (config.adminNotificationEmail || '').trim() || null,
        },
        user?.uid,
      );
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h2>Portal Automation</h2>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Controls how the client payment portal behaves after a card is attached.
      </p>

      {/* Not wrapped in .form-group — the shared CSS forces all inputs to
          full-width text-field styling, which mangles native checkboxes. */}
      <label
        htmlFor="autoActivateChk"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          cursor: 'pointer',
          marginTop: '1.25rem',
          padding: '0.75rem',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          background: config.autoActivateOnCardAttach ? '#f0f7ff' : '#fafafa',
        }}
      >
        <input
          id="autoActivateChk"
          type="checkbox"
          checked={!!config.autoActivateOnCardAttach}
          onChange={(e) => update({ autoActivateOnCardAttach: e.target.checked })}
          style={{
            appearance: 'auto',
            WebkitAppearance: 'checkbox',
            width: '18px',
            height: '18px',
            minWidth: '18px',
            marginTop: '2px',
            flexShrink: 0,
            cursor: 'pointer',
            accentColor: '#2962ff',
            border: '1px solid #9ca3af',
          }}
        />
        <span>
          <strong>Auto-activate retainer subscription on card attach</strong>
          <br />
          <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            When enabled, the client's Stripe recurring subscription starts immediately after they save a card in the portal. Requires the client to have a <code>monthlyRetainer</code> set.
          </span>
        </span>
      </label>

      <div className="form-group" style={{ marginTop: '1.25rem' }}>
        <label htmlFor="notifyOn">Notify admin on</label>
        <select
          id="notifyOn"
          value={config.notifyAdminOn || 'both'}
          onChange={(e) => update({ notifyAdminOn: e.target.value })}
        >
          {NOTIFY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="form-group" style={{ marginTop: '1rem' }}>
        <label htmlFor="notifyEmail">Admin notification email</label>
        <input
          id="notifyEmail"
          type="email"
          value={config.adminNotificationEmail || ''}
          onChange={(e) => update({ adminNotificationEmail: e.target.value })}
          placeholder="roseller@fusionict.com"
        />
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginTop: '1rem' }}>{error}</div>
      )}
      {saved && !dirty && (
        <div className="alert alert-success" style={{ marginTop: '1rem' }}>Saved.</div>
      )}

      <div style={{ marginTop: '1.5rem' }}>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default PortalAutomationSettings;
